import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ThemeProvider,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Box,
  Breadcrumbs,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { createTheme } from "@mui/material/styles";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getClients } from "../API/clientAPI";
import { getClientAddress } from "../API/clientAddressAPI";
import { getClientContact } from "../API/clientContactAPI";
import { getClientContactNumber } from "../API/clientContactNumberAPI";
import { getClientContactEmail } from "../API/clientContactEmailAPI";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

function ClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientResult = await getClients();

        if (Array.isArray(clientResult)) {
          const clientsData = await Promise.all(
            clientResult.map(async (client) => {
              const addresses = await getClientAddress(client.id);
              const contacts = await getClientContact(client.id);

              await Promise.all(
                contacts.map(async (contact) => {
                  const numbers = await getClientContactNumber(
                    client.id,
                    contact.id
                  );
                  const emails = await getClientContactEmail(
                    client.id,
                    contact.id
                  );

                  contact.numbers = numbers;
                  contact.emails = emails;
                })
              );

              return { ...client, addresses, contacts };
            })
          );

          setClients(clientsData);
          setLoading(false);
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredClients = clients.filter((client) => {
    const addressString = client.addresses
      ? client.addresses.map((addr) => addr.address).join(" ")
      : "";
    const contactNames = client.contacts
      ? client.contacts.map((c) => `${c.firstname} ${c.lastname}`).join(" ")
      : "";
    const contactNumbers = client.contacts
      ? client.contacts
          .flatMap((c) => c.numbers.map((n) => n.contact_number))
          .join(" ")
      : "";
    const contactEmails = client.contacts
      ? client.contacts.flatMap((c) => c.emails.map((e) => e.email)).join(" ")
      : "";

    return [
      client.name,
      addressString,
      contactNames,
      contactNumbers,
      contactEmails,
    ].some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <PeopleAltIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Client Retention</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>
                    Client Retention
                  </Typography>
                  <Typography
                    component={Link}
                    to="/list-clients"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    Clients
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />

          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Clients
                  </Typography>
                }
                action={
                  <Button
                    component={Link}
                    to="/create-client"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  >
                    Create Client
                  </Button>
                }
                sx={{ marginBottom: 2 }}
              />
              <TextField
                variant="outlined"
                placeholder="Search by client name, address, contact, number, or email..."
                value={searchQuery}
                onChange={handleSearchChange}
                fullWidth
                sx={{ marginBottom: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 2,
                    paddingBottom: "20px",
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body1" sx={{ marginLeft: 2 }}>
                    Loading clients...
                  </Typography>
                </Box>
              ) : error ? (
                <Typography variant="body1" color="error" sx={{ padding: 2 }}>
                  Error fetching clients: {error}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Address
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Contact
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Number
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Email
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                              textAlign: "center",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredClients.length > 0 ? (
                          filteredClients
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((client, index) => (
                              <TableRow key={index}>
                                <TableCell>{client.name}</TableCell>
                                <TableCell>
                                  {client.addresses[0]?.address}
                                </TableCell>
                                <TableCell>
                                  {client.contacts[0]?.lastname},{" "}
                                  {client.contacts[0]?.firstname}
                                </TableCell>
                                <TableCell>
                                  {client.contacts[0]?.numbers[0]?.type}:{" "}
                                  {
                                    client.contacts[0]?.numbers[0]
                                      ?.contact_number
                                  }
                                </TableCell>
                                <TableCell>
                                  {client.contacts[0]?.emails[0]?.email}
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Tooltip title="View">
                                      <IconButton
                                        component={Link}
                                        to={`/view-client/${client.id}`}
                                      >
                                        <VisibilityIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#6C757D",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Update">
                                      <IconButton
                                        component={Link}
                                        to={`/edit-client/${client.id}`}
                                      >
                                        <EditIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#007BFF",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        component={Link}
                                        to={`/delete-client/${client.id}`}
                                      >
                                        <DeleteIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#DC3545",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No clients found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredClients.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableContainer>
                </>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default ClientList;
